import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api.js';
import AbsenceModal from '../components/AbsenceModal.js';
import LatenessModal from '../components/LatenessModal.js';
import BatchWorkModal from '../components/BatchWorkModal.js'; 
import { useAuth } from '../contexts/AuthContext.js'; 
import { useDirtyForm } from '../contexts/DirtyFormContext.js';

// ⬇️ [모바일 고정 해제 1/3] baseStyles.filtersContainer.position을 'relative'로 변경
const baseStyles = {
  filtersContainer: { 
    background: '#f8f9fa', 
    padding: '15px', 
    borderRadius: '5px',
    position: 'relative', // ⬅️ 'sticky'에서 'relative'로 변경 (모바일 기본값)
    top: 0, 
    zIndex: 10, 
    borderBottom: '1px solid #dee2e6'
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  filterLabel: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
    color: '#555',
    paddingLeft: '2px',
  },
  select: { padding: '8px', fontSize: '1rem', width: '160px' }, 
  searchInput: { padding: '8px', fontSize: '1rem', width: '160px' }, 
  tableContainer: { marginTop: '20px', overflowX: 'auto' },
  table: { width: '100%', minWidth: '1200px', borderCollapse: 'collapse' },
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', fontSize: '0.9rem', whiteSpace: 'nowrap', textAlign: 'center', cursor: 'pointer' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.9rem', textAlign: 'center' },
  thCheckbox: { width: '40px', padding: '8px', background: '#f4f4f4', border: '1px solid #ddd', textAlign: 'center' },
  tdCheckbox: { width: '40px', padding: '8px', border: '1px solid #ddd', textAlign: 'center' },
  input: { width: '80px', padding: '5px', boxSizing: 'border-box', textAlign: 'center' },
  button: { padding: '5px 10px', cursor: 'pointer', border: 'none', borderRadius: '4px' },
  saveButton: { backgroundColor: '#28a745', color: 'white' },
  buttonDisabled: { backgroundColor: '#6c757d', color: '#e9ecef', cursor: 'not-allowed' },
  modalButton: { backgroundColor: '#17a2b8', color: 'white' },
  excelButton: { backgroundColor: '#28a745', color: 'white', padding: '8px 12px', fontSize: '1rem' },
  searchButton: { padding: '8px 12px', fontSize: '1rem', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' },
  statusButton: { padding: '8px 12px', fontSize: '1rem' },
  batchButton: { padding: '8px 12px', fontSize: '1rem', backgroundColor: '#007bff', color: 'white' },
  cancelButton: { backgroundColor: '#dc3545', color: 'white' },
  countDisplay: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
    marginTop: '15px', 
    paddingLeft: '5px' 
  },
  toast: {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    padding: '12px 20px',
    borderRadius: '8px',
    boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
    fontSize: '0.95rem',
    maxWidth: 'min(420px, 92vw)',
    textAlign: 'center',
    lineHeight: 1.45,
  },
  toastSuccess: { background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' },
  toastError: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' },
};

/** 서버 applyWorkSort와 동일한 기준으로 클라이언트 정렬 (검색 없이 헤더 클릭 시) */
function sortWorkData(rows, sortConfig) {
  const { sortBy, sortOrder } = sortConfig;
  return applyWorkSortLocal(rows.slice(), sortBy, sortOrder);
}

function applyWorkSortLocal(results, sortBy, sortOrder = 'ASC') {
  if (!sortBy) {
    return results.sort((a, b) => {
      const nameA = a.participant?.name || '';
      const nameB = b.participant?.name || '';
      return nameA.localeCompare(nameB, 'ko');
    });
  }
  const order = sortOrder.toUpperCase() === 'DESC' ? -1 : 1;
  return results.sort((a, b) => {
    let valueA;
    let valueB;
    switch (sortBy) {
      case 'name':
        valueA = a.participant?.name || '';
        valueB = b.participant?.name || '';
        return valueA.localeCompare(valueB, 'ko') * order;
      case 'plannedWorkHours':
        valueA = parseFloat(a.workRecord?.plannedWorkHours) || 0;
        valueB = parseFloat(b.workRecord?.plannedWorkHours) || 0;
        break;
      case 'totalWorkHours':
        valueA = (parseFloat(a.workRecord?.plannedWorkHours) || 0) - (a.totalAbsenceHours || 0) - (parseFloat(a.workRecord?.totalLatenessHours) || 0);
        valueB = (parseFloat(b.workRecord?.plannedWorkHours) || 0) - (b.totalAbsenceHours || 0) - (parseFloat(b.workRecord?.totalLatenessHours) || 0);
        break;
      case 'totalAbsenceHours':
        valueA = a.totalAbsenceHours || 0;
        valueB = b.totalAbsenceHours || 0;
        break;
      case 'totalLatenessHours':
        valueA = parseFloat(a.workRecord?.totalLatenessHours) || 0;
        valueB = parseFloat(b.workRecord?.totalLatenessHours) || 0;
        break;
      case 'workplace':
        valueA = a.participant?.workplace || '';
        valueB = b.participant?.workplace || '';
        return (valueA.localeCompare(valueB, 'ko')) * order;
      default:
        valueA = a.participant?.name || '';
        valueB = b.participant?.name || '';
        return valueA.localeCompare(valueB, 'ko') * order;
    }
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return (valueA - valueB) * order;
    }
    return 0;
  });
}

const WorkManagePage = () => {
  const { user } = useAuth(); 
  const { setIsDirty } = useDirtyForm();

  // ( ... state 선언 ... - 변경 없음)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); 
  const [searchTerms, setSearchTerms] = useState({
    searchName: '',
    searchWorkplace: '', 
    searchDistrict: '',
  });
  const searchTermsRef = useRef(searchTerms);
  searchTermsRef.current = searchTerms;
  const [appliedSearch, setAppliedSearch] = useState({
    searchName: '',
    searchWorkplace: '', 
    searchDistrict: '',
  });
  const [workData, setWorkData] = useState([]); 
  const [editingData, setEditingData] = useState({}); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isMonthClosed, setIsMonthClosed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState({ id: null, dates: [], reasons: {}, version: 0 });
  const [isLatenessModalOpen, setIsLatenessModalOpen] = useState(false);
  const [latenessModalTarget, setLatenessModalTarget] = useState({ id: null, details: [], version: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [currentlyEditingRowId, setCurrentlyEditingRowId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ sortBy: null, sortOrder: 'ASC' });
  const [hasSearched, setHasSearched] = useState(false);
  const [projectsStatusMap, setProjectsStatusMap] = useState({});
  /** 마감/마감해제 API + 이후 갱신 전체 구간 */
  const [monthActionLoading, setMonthActionLoading] = useState(false);
  /** 자동 사라지는 알림 { type: 'success'|'error', message } */
  const [toast, setToast] = useState(null);

  // ⬇️ [모바일 고정 해제 2/3] windowWidth state 추가
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // ⬇️ [모바일 고정 해제] windowWidth useEffect 추가
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ⬇️ [모바일 고정 해제 3/3] 동적 styles 변수 (position 속성 추가)
  const styles = {
    ...baseStyles,
    filtersContainer: {
      ...baseStyles.filtersContainer,
      // 1024px 초과 (데스크탑)일 때만 'sticky'로 변경하고 top: 60px
      position: windowWidth > 1024 ? 'sticky' : 'relative',
      top: windowWidth > 1024 ? '60px' : '0px', 
    }
  };

  // ( ... fetchProjects ... - 변경 없음)
  const fetchProjects = useCallback(async () => {
    if (!selectedYear) return;
    try {
      setError('');
      const res = await api.get(`/projects?year=${selectedYear}`);
      setProjects(res.data);
      if (!selectedProject) {
        setSelectedProject('all');
      }
    } catch (err) {
      console.error("사업 목록 로딩 실패", err);
      setError(err.response?.data?.message || '사업 목록 로딩 실패. 서버 연결을 확인해 주세요.');
    }
  }, [selectedYear, selectedProject]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const fetchProjectsStatus = useCallback(async () => {
    if (!selectedYear || !selectedMonth) return;
    try {
      const res = await api.get('/work/projects-status', { params: { year: selectedYear, month: selectedMonth } });
      setProjectsStatusMap(res.data || {});
    } catch {
      setProjectsStatusMap({});
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchProjectsStatus();
  }, [fetchProjectsStatus]);

  useEffect(() => {
    if (!toast) return undefined;
    const ms = toast.type === 'error' ? 6500 : 4200;
    const t = setTimeout(() => setToast(null), ms);
    return () => clearTimeout(t);
  }, [toast]);

  // ( ... fetchWorkRecords ... - 변경 없음)
  const fetchWorkRecords = useCallback(async (searchParamsOverride) => {
    if (!selectedYear || !selectedMonth) return;
    if (!selectedProject || selectedProject === 'all') return; // 전체 선택 시 데이터 미조회 (전체 마감 전용)
    const search = searchParamsOverride !== undefined ? searchParamsOverride : appliedSearch;
    setLoading(true);
    setError('');
    setMessage('');
    setSelectedIds(new Set());
    setIsDirty(false); 
    setCurrentlyEditingRowId(null); 
    try {
      const params = {
        year: selectedYear,
        projectId: selectedProject,
        month: selectedMonth,
        ...search,
      };
      if (sortConfig.sortBy) {
        params.sortBy = sortConfig.sortBy;
        params.sortOrder = sortConfig.sortOrder;
      }
      const res = await api.get('/work', { params });
      setWorkData(res.data); 
      const initialEditingData = {};
      res.data.forEach(item => {
        const workRecord = item.workRecord;
        // AbsenceReasons를 absenceReasons 객체로 변환
        const absenceReasons = {};
        if (workRecord.AbsenceReasons && Array.isArray(workRecord.AbsenceReasons)) {
          workRecord.AbsenceReasons.forEach(ar => {
            absenceReasons[ar.date] = ar.reason || '';
          });
        }
        // absenceDates에 있지만 AbsenceReasons에 없는 날짜는 빈 문자열로 처리
        if (workRecord.absenceDates && Array.isArray(workRecord.absenceDates)) {
          workRecord.absenceDates.forEach(date => {
            if (!absenceReasons[date]) {
              absenceReasons[date] = '';
            }
          });
        }
        const latenessDetails = (workRecord.LatenessDetails && Array.isArray(workRecord.LatenessDetails))
          ? workRecord.LatenessDetails.map(ld => ({ date: ld.date, type: ld.type || 'late', hours: ld.hours, reason: ld.reason || '' }))
          : [];
        initialEditingData[workRecord.id] = { 
          ...workRecord, 
          absenceReasons: absenceReasons,
          latenessDetails: latenessDetails
        };
      });
      setEditingData(initialEditingData); 
      const closed = res.data.length > 0 && res.data[0].workRecord.status === 'closed';
      setIsMonthClosed(closed);
    } catch (err) {
      setError(err.response?.data?.message || '근무 기록 로딩 실패');
      setWorkData([]);
      setEditingData({});
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedProject, selectedMonth, appliedSearch, sortConfig, setIsDirty]);

  // 사업명 '전체': 읽기 전용 목록 + 전체 마감 상태 (근무기록 자동 생성 없음)
  const fetchWorkRecordsOverview = useCallback(async (searchParamsOverride) => {
    if (!selectedYear || !selectedMonth) return;
    const search = searchParamsOverride !== undefined ? searchParamsOverride : appliedSearch;
    setLoading(true);
    setError('');
    setMessage('');
    setSelectedIds(new Set());
    setIsDirty(false);
    setCurrentlyEditingRowId(null);
    setEditingData({});
    try {
      const params = {
        year: selectedYear,
        month: selectedMonth,
        ...search,
      };
      if (sortConfig.sortBy) {
        params.sortBy = sortConfig.sortBy;
        params.sortOrder = sortConfig.sortOrder;
      }
      const [overviewRes, bulkRes] = await Promise.all([
        api.get('/work/overview', { params }),
        api.get('/work/bulk-status', {
          params: { year: selectedYear, month: selectedMonth },
        }),
      ]);
      setWorkData(overviewRes.data);
      setIsMonthClosed(bulkRes.data.allClosed);
    } catch (err) {
      setError(err.response?.data?.message || '목록 조회 실패');
      setWorkData([]);
      setIsMonthClosed(false);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, appliedSearch, sortConfig, setIsDirty]);

  const fetchWorkRecordsRef = useRef(fetchWorkRecords);
  fetchWorkRecordsRef.current = fetchWorkRecords;

  /** 연도·월·사업명 변경 시 이전 목록을 비우고 다시 검색하도록 유도 */
  useEffect(() => {
    setWorkData([]);
    setEditingData({});
    setSelectedIds(new Set());
    setCurrentlyEditingRowId(null);
    setHasSearched(false);
    setMessage('');
    setError('');
  }, [selectedYear, selectedMonth, selectedProject]);

  /** 단일 사업단 선택 시(또는 연·월 변경 시 단일 사업) 현재 검색어로 자동 조회 — 전체(all)는 제외 */
  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;
    if (!selectedProject || selectedProject === 'all' || selectedProject === '') return;
    const st = searchTermsRef.current;
    setAppliedSearch(st);
    setHasSearched(true);
    fetchWorkRecordsRef.current(st);
  }, [selectedYear, selectedMonth, selectedProject]);

  const syncMonthClosedFromApi = useCallback(async () => {
    try {
      if (selectedProject === 'all') {
        const res = await api.get('/work/bulk-status', {
          params: { year: selectedYear, month: selectedMonth },
        });
        setIsMonthClosed(res.data.allClosed);
      } else if (selectedProject) {
        const res = await api.get('/work/project-status', {
          params: { year: selectedYear, month: selectedMonth, projectId: selectedProject },
        });
        setIsMonthClosed(res.data.closed);
      }
    } catch {
      setIsMonthClosed(false);
    }
  }, [selectedYear, selectedMonth, selectedProject]);

  useEffect(() => {
    syncMonthClosedFromApi();
  }, [syncMonthClosedFromApi]);

  // ( ... 'Smart Dirty' useEffect ... - 변경 없음)
  useEffect(() => {
    if (workData.length === 0) {
      setIsDirty(false); 
      return;
    }
    let isActuallyDirty = false;
    for (const item of workData) {
      const originalRecord = item.workRecord;
      if (!originalRecord) continue;
      const editingRecord = editingData[originalRecord.id];
      if (!editingRecord) continue;
      const originalPlanned = String(originalRecord.plannedWorkHours || 0);
      const editedPlanned = String(editingRecord.plannedWorkHours || 0);
      const originalLateness = String(originalRecord.totalLatenessHours || 0);
      const editedLatenessTotal = editingRecord.latenessDetails && editingRecord.latenessDetails.length > 0
        ? String(editingRecord.latenessDetails.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0))
        : String(editingRecord.totalLatenessHours || 0);
      const originalAbsence = JSON.stringify(originalRecord.absenceDates || []);
      const editedAbsence = JSON.stringify(editingRecord.absenceDates || []);
      if (originalPlanned !== editedPlanned || originalLateness !== editedLatenessTotal || originalAbsence !== editedAbsence) {
        isActuallyDirty = true;
        break; 
      }
    }
    setIsDirty(isActuallyDirty);
  }, [editingData, workData, setIsDirty]);

  // ( ... handleSearchChange ... - 변경 없음)
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerms(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchClick = async () => {
    setAppliedSearch(searchTerms);
    setHasSearched(true);
    setError('');
    setMessage('');
    if (selectedProject === 'all') {
      await fetchWorkRecordsOverview(searchTerms);
    } else {
      await fetchWorkRecords(searchTerms);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  // 정렬: 목록이 이미 있을 때는 클라이언트만 재정렬 (검색 버튼 없이 API 재호출 안 함)
  const handleSort = (columnName) => {
    let sortOrder = 'ASC';
    if (sortConfig.sortBy === columnName && sortConfig.sortOrder === 'ASC') {
      sortOrder = 'DESC';
    }
    const nextConfig = { sortBy: columnName, sortOrder };
    setSortConfig(nextConfig);
    setWorkData((prev) => {
      if (prev.length === 0) return prev;
      return sortWorkData(prev, nextConfig);
    });
  };

  // 엑셀 출력 핸들러
  const handleExcelExport = useCallback(async () => {
    if (selectedProject === 'all') {
      alert('사업명을 특정 사업단으로 선택한 뒤 검색하면 엑셀 출력을 사용할 수 있습니다.');
      return;
    }
    if (currentlyEditingRowId !== null) {
      alert('수정 중인 항목을 먼저 저장하거나 취소한 후 엑셀 출력을 시도해주세요.');
      return;
    }
    try {
      const params = {
        year: selectedYear,
        projectId: selectedProject,
        month: selectedMonth,
        ...appliedSearch
      };
      const res = await api.get('/work/export', {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `work_records_${selectedYear}_${selectedMonth}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || '엑셀 출력 실패');
    }
  }, [selectedYear, selectedProject, selectedMonth, appliedSearch, currentlyEditingRowId]);

  // ( ... handleEditChange ... - 변경 없음)
  const handleEditChange = (workRecordId, field, value) => {
    setCurrentlyEditingRowId(workRecordId);
    setEditingData(prev => ({
      ...prev,
      [workRecordId]: { ...prev[workRecordId], [field]: value }
    }));
  };

  // ( ... handleSaveRow ... - 변경 없음)
  const handleSaveRow = async (item) => {
    const workRecordId = item.workRecord.id;
    const dataToSave = editingData[workRecordId];
    if (!dataToSave) return;
    const planned = parseFloat(dataToSave.plannedWorkHours || 0);
    const lateness = dataToSave.latenessDetails?.length > 0
      ? dataToSave.latenessDetails.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0)
      : parseFloat(dataToSave.totalLatenessHours || 0);
    const absence = parseFloat(item.totalAbsenceHours || 0); 
    const calculatedTotal = planned - absence - lateness;
    
    // absenceReasons 배열 생성
    const absenceReasons = dataToSave.absenceDates?.map(date => ({
      date: date,
      reason: dataToSave.absenceReasons?.[date] || ''
    })) || [];
    
    const latenessDetails = (dataToSave.latenessDetails || []).map(d => ({
      date: d.date, type: d.type || 'late', hours: parseFloat(d.hours) || 0, reason: d.reason || ''
    }));
    
    try {
      await api.put(`/work/${workRecordId}`, {
        plannedWorkHours: dataToSave.plannedWorkHours,
        totalWorkHours: calculatedTotal, 
        latenessDetails,
        absenceDates: dataToSave.absenceDates,
        absenceReasons: absenceReasons,
        version: dataToSave.version
      });
      setMessage('저장되었습니다.');
      fetchWorkRecords(); 
    } catch (err) {
      setIsDirty(true); 
      let errorMessage = '저장 실패: ' + (err.response?.data?.message || err.message);
      if (err.response && err.response.status === 409) {
        errorMessage = err.response?.data?.message || err.message;
      }
      setError(errorMessage);
    }
  };

  // ( ... handleCancelEdit ... - 변경 없음)
  const handleCancelEdit = () => {
    if (window.confirm('현재 행의 수정사항을 취소하고 원래 값으로 되돌리시겠습니까?')) {
      fetchWorkRecords(); 
    }
  };

  const handleAbsenceClick = (workRecordId) => {
    if (currentlyEditingRowId !== null && currentlyEditingRowId !== workRecordId) {
      alert('먼저 수정 중인 항목을 저장 또는 취소해주세요.');
      return;
    }
    setCurrentlyEditingRowId(workRecordId);
    const currentData = editingData[workRecordId];
    const workRecord = workData.find(item => item.workRecord.id === workRecordId)?.workRecord;
    
    // absenceDates 가져오기: editingData에 있으면 그것을, 없으면 workRecord에서 가져오기
    const absenceDates = currentData?.absenceDates || workRecord?.absenceDates || [];
    
    // AbsenceReasons를 reasons 객체로 변환
    const reasonsMap = {};
    if (workRecord?.AbsenceReasons) {
      workRecord.AbsenceReasons.forEach(ar => {
        reasonsMap[ar.date] = ar.reason || '';
      });
    }
    
    // absenceDates에 있지만 AbsenceReasons에 없는 날짜는 빈 문자열로 처리
    absenceDates.forEach(date => {
      if (!reasonsMap[date]) {
        reasonsMap[date] = '';
      }
    });
    
    setModalTarget({
      id: workRecordId,
      dates: absenceDates,
      reasons: reasonsMap,
      version: currentData?.version || workRecord?.version || 0
    });
    setIsModalOpen(true);
  };

  const handleLatenessClick = (workRecordId) => {
    if (currentlyEditingRowId !== null && currentlyEditingRowId !== workRecordId) {
      alert('먼저 수정 중인 항목을 저장 또는 취소해주세요.');
      return;
    }
    setCurrentlyEditingRowId(workRecordId);
    const currentData = editingData[workRecordId];
    const workRecord = workData.find(item => item.workRecord.id === workRecordId)?.workRecord;
    let details = currentData?.latenessDetails ?? (workRecord?.LatenessDetails && Array.isArray(workRecord.LatenessDetails)
      ? workRecord.LatenessDetails.map(ld => ({ date: ld.date, type: ld.type || 'late', hours: ld.hours, reason: ld.reason || '' }))
      : []);
    // [방법2] LatenessDetails는 없지만 totalLatenessHours가 있으면 → 합성 항목으로 미리 채움 (지각/조퇴 선택 후 저장)
    if (details.length === 0 && (parseFloat(workRecord?.totalLatenessHours) || 0) > 0) {
      const firstDay = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      details = [{ date: firstDay, type: 'late', hours: parseFloat(workRecord.totalLatenessHours), reason: '' }];
    }
    setLatenessModalTarget({
      id: workRecordId,
      details,
      version: currentData?.version || workRecord?.version || 0
    });
    setIsLatenessModalOpen(true);
  };

  // ( ... handleSaveAbsence ... - 변경 없음)
  const handleSaveAbsence = async (workRecordId, absenceData) => {
    const currentVersion = editingData[workRecordId]?.version;
    // 원본 데이터 백업
    const originalWorkRecord = workData.find(item => item.workRecord.id === workRecordId)?.workRecord;
    const originalAbsenceDates = originalWorkRecord?.absenceDates || [];
    
    try {
      await api.put(`/work/${workRecordId}`, {
        ...absenceData,
        version: currentVersion
      });
      setMessage('결근일이 저장되었습니다.');
      fetchWorkRecords(); 
    } catch (err) {
      // 저장 실패 시 editingData를 원본 상태로 되돌리기
      if (originalWorkRecord) {
        setEditingData(prev => ({
          ...prev,
          [workRecordId]: {
            ...originalWorkRecord,
            absenceDates: originalAbsenceDates
          }
        }));
      }
      
      // 저장 실패 시 변경사항이 없으므로 isDirty를 false로 설정
      setIsDirty(false);
      
      let errorMessage = '결근일 저장 실패: ' + (err.response?.data?.message || err.message);
      if (err.response && err.response.status === 409) {
        errorMessage = err.response?.data?.message || err.message;
        fetchWorkRecords(); 
      }
      alert(errorMessage);
    }
  };

  const handleSaveLateness = async (workRecordId, latenessData) => {
    const currentVersion = editingData[workRecordId]?.version;
    const originalWorkRecord = workData.find(item => item.workRecord.id === workRecordId)?.workRecord;
    try {
      await api.put(`/work/${workRecordId}`, {
        ...latenessData,
        version: currentVersion
      });
      setMessage('지각/조퇴/산재요양이 저장되었습니다.');
      fetchWorkRecords();
    } catch (err) {
      if (originalWorkRecord) {
        setEditingData(prev => ({
          ...prev,
          [workRecordId]: { ...originalWorkRecord }
        }));
      }
      setIsDirty(false);
      let errorMessage = '지각/조퇴/산재요양 저장 실패: ' + (err.response?.data?.message || err.message);
      if (err.response?.status === 409) {
        errorMessage = err.response?.data?.message || err.message;
        fetchWorkRecords();
      }
      alert(errorMessage);
    }
  };

  // ( ... handleCloseModal ... - 변경 없음)
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentlyEditingRowId(null);
    
    // 모달 닫을 때 editingData를 원본 상태로 되돌리기
    if (modalTarget?.id) {
      const originalWorkRecord = workData.find(item => item.workRecord.id === modalTarget.id)?.workRecord;
      if (originalWorkRecord) {
        setEditingData(prev => ({
          ...prev,
          [modalTarget.id]: {
            ...originalWorkRecord
          }
        }));
      }
    }
  };

  const handleCloseLatenessModal = () => {
    setIsLatenessModalOpen(false);
    setCurrentlyEditingRowId(null);
    if (latenessModalTarget?.id) {
      const item = workData.find(i => i.workRecord.id === latenessModalTarget.id);
      const originalWorkRecord = item?.workRecord;
      if (originalWorkRecord) {
        const latenessDetails = (originalWorkRecord.LatenessDetails && Array.isArray(originalWorkRecord.LatenessDetails))
          ? originalWorkRecord.LatenessDetails.map(ld => ({ date: ld.date, type: ld.type || 'late', hours: ld.hours, reason: ld.reason || '' }))
          : [];
        const absenceReasons = {};
        if (originalWorkRecord.AbsenceReasons) {
          originalWorkRecord.AbsenceReasons.forEach(ar => { absenceReasons[ar.date] = ar.reason || ''; });
        }
        (originalWorkRecord.absenceDates || []).forEach(date => { if (!absenceReasons[date]) absenceReasons[date] = ''; });
        setEditingData(prev => ({
          ...prev,
          [latenessModalTarget.id]: { ...originalWorkRecord, latenessDetails, absenceReasons }
        }));
      }
    }
  };

  const handleLatenessChangeInModal = (newDetails) => {
    const workRecordId = latenessModalTarget.id;
    if (!workRecordId) return;
    setCurrentlyEditingRowId(workRecordId);
    setEditingData(prev => {
      const currentData = prev[workRecordId] || {};
      return {
        ...prev,
        [workRecordId]: { ...currentData, latenessDetails: newDetails }
      };
    });
    setLatenessModalTarget(prev => ({ ...prev, details: newDetails }));
  };
  
  const handleAbsenceChangeInModal = (newDates, newReasons) => {
    const workRecordId = modalTarget.id;
    if (!workRecordId) return;
    setCurrentlyEditingRowId(workRecordId); 
    
    // editingData 업데이트: absenceDates와 absenceReasons 모두 저장
    setEditingData(prev => {
      const currentData = prev[workRecordId] || {};
      return {
        ...prev,
        [workRecordId]: { 
          ...currentData, 
          absenceDates: newDates,
          absenceReasons: newReasons || {}
        }
      };
    });
    
    // modalTarget도 업데이트
    setModalTarget(prev => ({ 
      ...prev, 
      dates: newDates,
      reasons: newReasons || {} 
    }));
  };

  // ( ... handleMonthStatusToggle, handleSelectOne, handleSelectAll, handleBatchSave ... - 변경 없음)
  const handleMonthStatusToggle = async () => {
    if (currentlyEditingRowId) { 
      alert('수정 중인 항목을 저장 또는 취소해주세요.');
      return;
    }
    const newStatus = isMonthClosed ? 'open' : 'closed';
    const actionText = selectedProject === 'all'
      ? (isMonthClosed ? '전체 마감 해제' : '전체 마감')
      : (isMonthClosed ? '마감 해제' : '마감');
    if (!window.confirm(`[${selectedYear}년 ${selectedMonth}월] ${actionText} 처리를 하시겠습니까?`)) {
      return;
    }
    setMonthActionLoading(true);
    setLoading(true);
    setError('');
    setMessage('');
    setToast(null);
    try {
      const res = await api.post('/work/status', {
        year: selectedYear,
        projectId: selectedProject === 'all' ? 'all' : selectedProject,
        month: selectedMonth,
        newStatus: newStatus,
      });
      const successMsg = res.data?.message || '처리되었습니다.';
      await fetchProjectsStatus();
      if (hasSearched) {
        if (selectedProject === 'all') {
          await fetchWorkRecordsOverview();
        } else {
          await fetchWorkRecords();
        }
      } else {
        await syncMonthClosedFromApi();
      }
      setToast({ type: 'success', message: successMsg });
    } catch (err) {
      const errMsg = err.response?.data?.message || `${actionText} 처리 실패`;
      setError(errMsg);
      setToast({ type: 'error', message: errMsg });
    } finally {
      setMonthActionLoading(false);
      setLoading(false);
    }
  };
  const handleSelectOne = (workRecordId) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(workRecordId)) {
      newSelectedIds.delete(workRecordId);
    } else {
      newSelectedIds.add(workRecordId);
    }
    setSelectedIds(newSelectedIds);
  };
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = workData.filter((item) => item.workRecord?.id).map((item) => item.workRecord.id);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  };
  const handleBatchSave = async (batchValues) => {
    const idsToUpdate = Array.from(selectedIds);
    if (idsToUpdate.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    const dataToUpdate = {};
    if (batchValues.plannedWorkHours !== '') dataToUpdate.plannedWorkHours = parseFloat(batchValues.plannedWorkHours);
    if (Object.keys(dataToUpdate).length === 0) {
      alert('유효한 수정 값이 없습니다.');
      return;
    }
    setLoading(true);
    try {
      await api.put('/work/batch', {
        ids: idsToUpdate,
        data: dataToUpdate 
      });
      setMessage(`${idsToUpdate.length}개 항목이 일괄 수정(자동계산)되었습니다.`);
      fetchWorkRecords(); 
    } catch (err) {
      let errorMessage = '일괄 수정 실패: ' + (err.response?.data?.message || err.message);
      if (err.response && err.response.status === 409) {
        errorMessage = err.response?.data?.message || err.message;
        fetchWorkRecords(); 
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
      setIsBatchModalOpen(false);
    }
  };

  // --- 렌더링용 ---
  const monthOptions = [];
  for (let m = 1; m <= 12; m++) { monthOptions.push(m); }
  const isReadOnlyAll = selectedProject === 'all';
  const selectableCount = workData.filter((item) => item.workRecord?.id).length;
  const isAllSelected = selectableCount > 0 && selectedIds.size === selectableCount;


  return (
    <div>
      <h2>참여자 근무 관리</h2>

      {/* 1. 필터 (변경 없음) */}
      <div style={styles.filtersContainer}>
         <div style={styles.filterGroup}>
           <div style={styles.filterItem}>
            <span style={styles.filterLabel}>연도</span>
            <input
              type="number"
              style={styles.select} 
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              placeholder="연도 입력 (예: 2024)"
              min="2020"
              step="1"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>월</span>
            <select style={styles.select} value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
              {monthOptions.map(m => <option key={m} value={m}>{m}월</option>)}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사업명</span>
            <select style={styles.select} value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
              <option value="">-- 사업명 선택 --</option>
              <option value="all">전체</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.projectName}({projectsStatusMap[p.id] ? '마감' : '미마감'})</option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>성명</span>
            <input type="text" name="searchName" placeholder="성명 검색" value={searchTerms.searchName} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={styles.searchInput} />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>근무장소</span>
            <input type="text" name="searchWorkplace" placeholder="근무장소 검색" value={searchTerms.searchWorkplace} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={styles.searchInput} />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>읍면동</span>
            <input
              type="text"
              name="searchDistrict"
              placeholder="읍면동 검색"
              value={searchTerms.searchDistrict}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              style={styles.searchInput}
            />
          </div>

          <div style={{...styles.filterItem, marginLeft: 'auto', flexDirection: 'row', gap: '8px' }}>
            <button
              style={styles.searchButton}
              onClick={handleSearchClick}
              disabled={loading || monthActionLoading}
            >
              🔍 검색
            </button>
            <button
              style={{...styles.button, ...styles.excelButton, marginLeft: 0}}
              onClick={handleExcelExport}
              disabled={loading || monthActionLoading || workData.length === 0 || currentlyEditingRowId !== null || isReadOnlyAll}
            >
              엑셀 출력
            </button>
            <button
              style={{...styles.button, ...styles.batchButton, marginLeft: '10px'}}
              onClick={() => setIsBatchModalOpen(true)}
              disabled={loading || monthActionLoading || isMonthClosed || selectedIds.size === 0 || currentlyEditingRowId !== null || isReadOnlyAll}
            >
              선택일괄수정 ({selectedIds.size})
            </button>
            <button
              style={{
                ...styles.button, 
                ...styles.statusButton, 
                backgroundColor: isMonthClosed ? '#ffc107' : '#dc3545',
                color: isMonthClosed ? 'black' : 'white',
                marginLeft: '10px'
              }}
              onClick={handleMonthStatusToggle}
              disabled={loading || monthActionLoading || currentlyEditingRowId !== null || user?.role !== 'admin' || (selectedProject !== 'all' && workData.length === 0)}
              title={user?.role !== 'admin' ? '관리자만 마감/해제할 수 있습니다.' : selectedProject === 'all' ? '해당 연·월의 모든 사업 근무기록에 적용됩니다. 목록 필터와 무관합니다.' : ''}
            >
              {selectedProject === 'all'
                ? (isMonthClosed ? '전체 마감 해제' : '전체 마감하기')
                : (isMonthClosed ? '월 마감 해제' : '월 마감하기')}
            </button>
          </div>
        </div>
        
        {/* 인원 표시 ( ... 변경 없음 ... ) */}
        <div style={styles.countDisplay}>
          {monthActionLoading
            ? '마감 처리 중입니다. 잠시만 기다려 주세요…'
            : loading
            ? '검색 중...'
            : !hasSearched
              ? '연도·월·사업명을 선택한 뒤 검색을 누르면 목록이 조회됩니다.'
              : selectedProject === 'all'
                ? `조회된 인원: 총 ${workData.length}명 (전체 사업, 조회 전용 · 수정은 사업단 선택 후)`
                : `조회된 인원: 총 ${workData.length}명`}
        </div>

      </div>
      
      {message && <p style={{color: 'green', marginTop: '10px'}}>{message}</p>}
      {error && <p style={{color: 'red', marginTop: '10px'}}>{error}</p>}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            ...styles.toast,
            ...(toast.type === 'error' ? styles.toastError : styles.toastSuccess),
          }}
        >
          {toast.message}
        </div>
      )}

      {/* 2. 데이터 테이블 ( ... 변경 없음 ... ) */}
      <div style={styles.tableContainer}>
        {loading || monthActionLoading ? (
          <p>{monthActionLoading ? '마감 처리 중…' : '로딩 중...'}</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {!isReadOnlyAll && (
                  <th style={styles.thCheckbox}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      disabled={selectableCount === 0 || currentlyEditingRowId !== null}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}
                {isReadOnlyAll && <th style={styles.th}>사업단</th>}
                <th style={styles.th} onClick={() => handleSort('name')}>
                  성명 ↕
                </th>
                <th style={styles.th}>주민등록번호</th>
                <th style={styles.th} onClick={() => handleSort('workplace')}>근무장소 ↕</th>
                <th style={styles.th} onClick={() => handleSort('plannedWorkHours')}>
                  계획근무시간 ↕
                </th>
                <th style={styles.th} onClick={() => handleSort('totalWorkHours')}>
                  총근무시간 ↕
                </th>
                <th style={styles.th}>결근일</th>
                <th style={styles.th} onClick={() => handleSort('totalAbsenceHours')}>
                  총결근시간 ↕
                </th>
                <th style={styles.th} onClick={() => handleSort('totalLatenessHours')}>
                  지각/조퇴/요양 ↕
                </th>
                <th style={styles.th}>저장</th>
              </tr>
            </thead>
            <tbody>
              {workData.length > 0 ? workData.map((item) => {
                const p = item.participant;
                const originalRecord = item.workRecord;
                const projectLabel = p.Project?.projectName || p.project?.projectName || '—';

                if (isReadOnlyAll) {
                  if (!originalRecord) {
                    return (
                      <tr key={`p-${p.id}`}>
                        <td style={styles.td}>{projectLabel}</td>
                        <td style={styles.td}>{p.name}</td>
                        <td style={styles.td}>{p.rrn ? `${p.rrn.substring(0, 8)}...` : '—'}</td>
                        <td style={styles.td}>{p.workplace || '—'}</td>
                        <td style={styles.td}>—</td>
                        <td style={styles.td}>—</td>
                        <td style={styles.td}>0 일</td>
                        <td style={styles.td}>0 h</td>
                        <td style={styles.td}>0.0 h</td>
                        <td style={styles.td}><span style={{ color: '#6c757d' }}>근무기록 없음</span></td>
                      </tr>
                    );
                  }
                  const wr = originalRecord;
                  const planned = parseFloat(wr.plannedWorkHours || 0);
                  const latenessRead =
                    wr.LatenessDetails?.length > 0
                      ? wr.LatenessDetails.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0)
                      : parseFloat(wr.totalLatenessHours || 0);
                  const absence = parseFloat(item.totalAbsenceHours || 0);
                  const calculatedTotal = planned - absence - latenessRead;
                  return (
                    <tr key={wr.id}>
                      <td style={styles.td}>{projectLabel}</td>
                      <td style={styles.td}>{p.name}</td>
                      <td style={styles.td}>{p.rrn ? `${p.rrn.substring(0, 8)}...` : '—'}</td>
                      <td style={styles.td}>{p.workplace || '—'}</td>
                      <td style={styles.td}>{planned}</td>
                      <td style={styles.td}>{calculatedTotal}</td>
                      <td style={styles.td}>{wr.absenceDates?.length || 0} 일</td>
                      <td style={styles.td}>{item.totalAbsenceHours} h</td>
                      <td style={styles.td}>{latenessRead.toFixed(1)} h</td>
                      <td style={styles.td}><span style={{ color: '#6c757d' }}>—</span></td>
                    </tr>
                  );
                }

                if (!originalRecord) {
                  return null;
                }

                const wr = editingData[originalRecord.id] || {};
                const isChecked = selectedIds.has(wr.id);
                const planned = parseFloat(wr.plannedWorkHours || 0);
                const lateness = wr.latenessDetails?.length > 0
                  ? wr.latenessDetails.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0)
                  : parseFloat(wr.totalLatenessHours || 0);
                const absence = parseFloat(item.totalAbsenceHours || 0);
                const calculatedTotal = planned - absence - lateness;
                const originalPlanned = String(originalRecord.plannedWorkHours || 0);
                const editedPlanned = String(wr.plannedWorkHours || 0);
                const originalLateness = String(originalRecord.totalLatenessHours || 0);
                const editedLateness = wr.latenessDetails?.length > 0
                  ? String(wr.latenessDetails.reduce((s, d) => s + (parseFloat(d.hours) || 0), 0))
                  : String(wr.totalLatenessHours || 0);
                const originalAbsence = JSON.stringify(originalRecord.absenceDates || []);
                const editedAbsence = JSON.stringify(wr.absenceDates || []);
                const isRowDirty = (originalPlanned !== editedPlanned) || (originalLateness !== editedLateness) || (originalAbsence !== editedAbsence);
                const isAnotherRowEditing = currentlyEditingRowId !== null && currentlyEditingRowId !== wr.id;

                return (
                  <tr key={p.id} style={{backgroundColor: isAnotherRowEditing ? '#f8f9fa' : 'transparent'}}>
                    <td style={styles.tdCheckbox}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleSelectOne(wr.id)}
                        disabled={isMonthClosed || isAnotherRowEditing}
                      />
                    </td>
                    <td style={styles.td}>{p.name}</td>
                    <td style={styles.td}>{p.rrn ? `${p.rrn.substring(0, 8)}...` : '—'}</td>
                    <td style={styles.td}>{p.workplace}</td>
                    <td style={styles.td}>
                      <input type="number" style={styles.input} step="1"
                             value={wr.plannedWorkHours || 0}
                             disabled={isMonthClosed || isAnotherRowEditing}
                             onChange={(e) => handleEditChange(wr.id, 'plannedWorkHours', e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <input type="number" style={styles.input} step="1"
                             value={calculatedTotal}
                             disabled
                      />
                    </td>
                    <td style={styles.td}>
                      <button style={{...styles.button, ...styles.modalButton}}
                              disabled={isMonthClosed || isAnotherRowEditing}
                              onClick={() => handleAbsenceClick(wr.id)}>
                        {wr.absenceDates?.length || 0} 일
                      </button>
                    </td>
                    <td style={styles.td}>{item.totalAbsenceHours} h</td>
                    <td style={styles.td}>
                      <button
                        style={{ ...styles.button, ...styles.modalButton }}
                        disabled={isMonthClosed || isAnotherRowEditing}
                        onClick={() => handleLatenessClick(wr.id)}
                      >
                        {lateness.toFixed(1)} h
                      </button>
                    </td>
                    <td style={styles.td}>
                      {isRowDirty && currentlyEditingRowId === wr.id ? (
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                          <button style={{...styles.button, ...styles.saveButton}}
                                  disabled={isMonthClosed}
                                  onClick={() => handleSaveRow(item)}>
                            저장
                          </button>
                          <button style={{...styles.button, ...styles.cancelButton}}
                                  disabled={isMonthClosed}
                                  onClick={handleCancelEdit}>
                            취소
                          </button>
                        </div>
                      ) : (
                        <button style={{
                                ...styles.button,
                                ...styles.buttonDisabled
                              }}
                              disabled>
                          저장
                        </button>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="10" style={styles.td}>
                    {error || (hasSearched ? '해당 조건에 맞는 인원이 없습니다. 필터를 조정한 뒤 다시 검색하세요.' : '검색을 누르면 목록이 조회됩니다.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 3. 모달 ( ... 변경 없음 ... ) */}
      <AbsenceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveAbsence}
        onDatesChange={handleAbsenceChangeInModal} 
        currentDates={modalTarget.dates}
        currentReasons={modalTarget.reasons}
        workRecordId={modalTarget.id}
        currentVersion={modalTarget.version} 
        isMonthClosed={isMonthClosed}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
      />
      <LatenessModal
        isOpen={isLatenessModalOpen}
        onClose={handleCloseLatenessModal}
        onSave={handleSaveLateness}
        onDetailsChange={handleLatenessChangeInModal}
        currentDetails={latenessModalTarget.details}
        workRecordId={latenessModalTarget.id}
        currentVersion={latenessModalTarget.version}
        isMonthClosed={isMonthClosed}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
      />
      <BatchWorkModal
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        onSave={handleBatchSave}
      />
    </div>
  );
};

export default WorkManagePage;